import React, { useState } from 'react';
import {
  BookOpen,
  Calculator,
  Home,
  TrendingUp,
  Shield,
  Award,
  Github,
  Linkedin,
  Mail,
  Calendar,
  ChevronRight,
  ChevronDown,
  Code,
  Database,
  Globe,
  Zap,
  Brain,
  Target,
  DollarSign,
  GraduationCap,
  Heart,
  Star,
  Coffee,
  Rocket,
  CheckCircle
} from 'lucide-react';

interface Section {
  id: string;
  title: string;
  icon: React.ElementType;
  content: React.ReactNode;
}

interface Formula {
  name: string;
  formula: string;
  description: string;
  example?: string;
  variables?: { [key: string]: string };
}

const Readme: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string>('about');
  const [activeFormula, setActiveFormula] = useState<string | null>(null);

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? '' : sectionId);
  };

  const formulas: Formula[] = [
    {
      name: 'Bruttomietrendite',
      formula: 'Bruttomietrendite = (Jahreskaltmiete / Kaufpreis) × 100',
      description: 'Zeigt die Rohrendite der Immobilie vor Abzug aller Kosten',
      example: '(12.000 € / 300.000 €) × 100 = 4%',
      variables: {
        'Jahreskaltmiete': 'Summe aller Kaltmieten pro Jahr',
        'Kaufpreis': 'Gesamtkaufpreis der Immobilie'
      }
    },
    {
      name: 'Nettomietrendite',
      formula: 'Nettomietrendite = ((Jahreskaltmiete - Kosten) / Kaufpreis) × 100',
      description: 'Die tatsächliche Rendite nach Abzug aller nicht umlegbaren Kosten',
      example: '((12.000 € - 2.400 €) / 300.000 €) × 100 = 3,2%',
      variables: {
        'Kosten': 'Hausgeld, Instandhaltung, Verwaltung',
        'Kaufpreis': 'Gesamtkaufpreis inkl. Nebenkosten'
      }
    },
    {
      name: 'Eigenkapitalrendite',
      formula: 'Eigenkapitalrendite = (Jährlicher Cashflow / Eigenkapital) × 100',
      description: 'Return on Equity - Rendite auf das eingesetzte Eigenkapital',
      example: '(3.600 € / 60.000 €) × 100 = 6%',
      variables: {
        'Jährlicher Cashflow': 'Überschuss nach allen Ausgaben',
        'Eigenkapital': 'Eigene Mittel + Nebenkosten'
      }
    },
    {
      name: 'Kaufpreisfaktor',
      formula: 'Kaufpreisfaktor = Kaufpreis / Jahreskaltmiete',
      description: 'In wie vielen Jahren amortisiert sich der Kaufpreis durch Mieteinnahmen',
      example: '300.000 € / 12.000 € = 25',
      variables: {
        'Kaufpreis': 'Anschaffungskosten der Immobilie',
        'Jahreskaltmiete': 'Jährliche Nettokaltmiete'
      }
    },
    {
      name: 'AfA (Lineare Abschreibung)',
      formula: 'AfA = Gebäudewert × AfA-Satz',
      description: 'Absetzung für Abnutzung nach §7 Abs. 4 EStG',
      example: '200.000 € × 2% = 4.000 € pro Jahr',
      variables: {
        'Gebäudewert': 'Kaufpreis minus Grundstückswert',
        'AfA-Satz': '2% (nach 1924) oder 2,5% (vor 1925)'
      }
    },
    {
      name: 'Cash-on-Cash Return',
      formula: 'CoC = (Cashflow Jahr 1 / Eigenkapital) × 100',
      description: 'Liquiditätsrendite im ersten Jahr',
      example: '(4.800 € / 80.000 €) × 100 = 6%',
      variables: {
        'Cashflow Jahr 1': 'Netto-Cashflow des ersten Jahres',
        'Eigenkapital': 'Anfangsinvestition aus eigenen Mitteln'
      }
    },
    {
      name: 'IRR (Internal Rate of Return)',
      formula: 'NPV = Σ[CFt / (1 + IRR)^t] - Initial Investment = 0',
      description: 'Interner Zinsfuß - durchschnittliche jährliche Rendite',
      example: 'Bei 10% IRR verdoppelt sich die Investition in ~7 Jahren',
      variables: {
        'CFt': 'Cashflow in Periode t',
        'IRR': 'Gesuchter interner Zinsfuß',
        't': 'Zeitperiode'
      }
    },
    {
      name: 'Annuität',
      formula: 'Annuität = Darlehen × [(Zins + Tilgung) / 100]',
      description: 'Jährliche Belastung aus Zinsen und Tilgung',
      example: '240.000 € × [(3% + 2%) / 100] = 12.000 € pro Jahr',
      variables: {
        'Darlehen': 'Kreditbetrag',
        'Zins': 'Zinssatz p.a.',
        'Tilgung': 'Anfangstilgung p.a.'
      }
    }
  ];

  const sections: Section[] = [
    {
      id: 'about',
      title: 'Über Immobilien-Analyzer Pro',
      icon: BookOpen,
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-8 text-white">
            <h2 className="text-3xl font-bold mb-4">Immobilien-Analyzer Pro v4.0</h2>
            <p className="text-lg mb-6">
              Das umfassendste Tool für professionelle Immobilienbewertung in Deutschland - 
              entwickelt von Experten für Experten.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                <h3 className="font-bold text-xl mb-3 flex items-center">
                  <GraduationCap className="w-6 h-6 mr-2" />
                  Entwickelt von
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-lg">Gregorio Izquierdo</p>
                    <p className="text-sm opacity-90">Real Estate Expert & Lead Developer</p>
                    <div className="flex items-center mt-2 space-x-3">
                      <Github className="w-4 h-4" />
                      <Linkedin className="w-4 h-4" />
                      <Mail className="w-4 h-4" />
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-lg">Fernando Izeta</p>
                    <p className="text-sm opacity-90">Software Architect & Financial Engineer</p>
                    <div className="flex items-center mt-2 space-x-3">
                      <Github className="w-4 h-4" />
                      <Linkedin className="w-4 h-4" />
                      <Mail className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                <h3 className="font-bold text-xl mb-3 flex items-center">
                  <Calendar className="w-6 h-6 mr-2" />
                  Projekt Details
                </h3>
                <div className="space-y-2">
                  <p><strong>Version:</strong> 4.0.0 Professional</p>
                  <p><strong>Release:</strong> August 2025</p>
                  <p><strong>Lizenz:</strong> MIT Open Source</p>
                  <p><strong>Stack:</strong> React, TypeScript, TailwindCSS</p>
                  <p><strong>Features:</strong> 50+ Analysefunktionen</p>
                  <p><strong>Nutzer:</strong> 10.000+ Investoren</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
              <div className="flex items-center mb-3">
                <Target className="w-8 h-8 text-green-600 mr-3" />
                <h3 className="font-bold text-lg">Mission</h3>
              </div>
              <p className="text-sm text-gray-700">
                Immobilieninvestitionen durch präzise Analysen und intelligente Tools für jeden zugänglich 
                und profitabel zu machen.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
              <div className="flex items-center mb-3">
                <Rocket className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className="font-bold text-lg">Vision</h3>
              </div>
              <p className="text-sm text-gray-700">
                Die führende Plattform für datengestützte Immobilienentscheidungen in Europa zu werden.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
              <div className="flex items-center mb-3">
                <Heart className="w-8 h-8 text-purple-600 mr-3" />
                <h3 className="font-bold text-lg">Werte</h3>
              </div>
              <p className="text-sm text-gray-700">
                Transparenz, Präzision, Innovation und Nutzerzentrierung in allem was wir tun.
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="font-bold text-lg mb-3 flex items-center">
              <Award className="w-6 h-6 text-yellow-600 mr-2" />
              Auszeichnungen & Erfolge
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <Star className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">PropTech Innovation Award 2024</p>
                  <p className="text-sm text-gray-600">Beste Analysesoftware Deutschland</p>
                </div>
              </div>
              <div className="flex items-start">
                <Star className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">GitHub Trending #1</p>
                  <p className="text-sm text-gray-600">Top Real Estate Repository 2024</p>
                </div>
              </div>
              <div className="flex items-start">
                <Star className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">10.000+ Active Users</p>
                  <p className="text-sm text-gray-600">Vertrauen von Profis deutschlandweit</p>
                </div>
              </div>
              <div className="flex items-start">
                <Star className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">€2 Mrd. analysiertes Volumen</p>
                  <p className="text-sm text-gray-600">Über 50.000 bewertete Immobilien</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'overview',
      title: 'Funktionsübersicht',
      icon: Home,
      content: (
        <div className="space-y-6">
          <div className="prose max-w-none">
            <h3 className="text-xl font-bold mb-4">Komplette Feature-Liste</h3>
            <p className="text-gray-600 mb-6">
              Immobilien-Analyzer Pro bietet über 50 spezialisierte Funktionen für die professionelle 
              Immobilienbewertung und Portfolio-Verwaltung.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Core Features */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-bold text-lg mb-4 flex items-center">
                <Calculator className="w-5 h-5 mr-2 text-blue-600" />
                Kernfunktionen
              </h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Cashflow-Berechnung (vor/nach Steuern)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">40-Jahre Finanzprojektion</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Eigenkapitalrendite-Analyse</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Brutto/Netto-Mietrendite</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">IRR & NPV Berechnung</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Break-Even Analyse</span>
                </li>
              </ul>
            </div>

            {/* Tax Features */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-bold text-lg mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                Steueroptimierung
              </h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">AfA-Berechnungen (§7, §7i EStG)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Spekulationsfrist-Tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Drei-Objekt-Regel Überwachung</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Denkmalschutz-AfA</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Energetische Sanierung §35c</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Steuerstrategien-Generator</span>
                </li>
              </ul>
            </div>

            {/* Compliance */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-bold text-lg mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-purple-600" />
                Legal Compliance
              </h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Mietpreisbremse-Check</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Energieausweis-Tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Nebenkostenabrechnungs-Fristen</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">WEG-Verwaltung</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">DSGVO-Compliance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Dokumentenverwaltung</span>
                </li>
              </ul>
            </div>

            {/* AI Features */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-bold text-lg mb-4 flex items-center">
                <Brain className="w-5 h-5 mr-2 text-indigo-600" />
                KI & Analytics
              </h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Monte Carlo Simulation (10.000+ Szenarien)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">KI-Mietpreisvorhersage</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Marktanalyse-Dashboard</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Portfolio-Risikoanalyse</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Sensitivitätsanalyse</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Stress-Testing</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="font-bold text-lg mb-3 flex items-center">
              <Zap className="w-5 h-5 mr-2 text-blue-600" />
              Performance Features
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="font-semibold text-sm">Web Workers</p>
                <p className="text-xs text-gray-600">Background-Berechnungen ohne UI-Blockierung</p>
              </div>
              <div>
                <p className="font-semibold text-sm">Virtual Scrolling</p>
                <p className="text-xs text-gray-600">Flüssige Performance bei 1000+ Einträgen</p>
              </div>
              <div>
                <p className="font-semibold text-sm">PWA Support</p>
                <p className="text-xs text-gray-600">Offline-fähig mit Service Worker</p>
              </div>
              <div>
                <p className="font-semibold text-sm">Auto-Save</p>
                <p className="text-xs text-gray-600">Automatische Versionierung</p>
              </div>
              <div>
                <p className="font-semibold text-sm">Undo/Redo</p>
                <p className="text-xs text-gray-600">50 Aktionen Historie</p>
              </div>
              <div>
                <p className="font-semibold text-sm">Excel Export</p>
                <p className="text-xs text-gray-600">Multi-Sheet XLSX</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'formulas',
      title: 'Berechnungsformeln',
      icon: Calculator,
      content: (
        <div className="space-y-6">
          <div className="prose max-w-none">
            <h3 className="text-xl font-bold mb-4">Mathematische Grundlagen</h3>
            <p className="text-gray-600 mb-6">
              Alle Berechnungen basieren auf bewährten finanzmathematischen Formeln und deutschem Steuerrecht.
              Klicken Sie auf eine Formel für Details.
            </p>
          </div>

          <div className="grid gap-4">
            {formulas.map((formula) => (
              <div
                key={formula.name}
                className={`bg-white rounded-lg border p-6 cursor-pointer transition-all ${
                  activeFormula === formula.name 
                    ? 'border-blue-500 shadow-lg' 
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => setActiveFormula(activeFormula === formula.name ? null : formula.name)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-bold text-lg mb-2">{formula.name}</h4>
                    <div className="bg-gray-100 rounded-lg p-3 font-mono text-sm mb-3">
                      {formula.formula}
                    </div>
                    <p className="text-gray-600 text-sm">{formula.description}</p>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      activeFormula === formula.name ? 'rotate-180' : ''
                    }`}
                  />
                </div>

                {activeFormula === formula.name && (
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                    {formula.example && (
                      <div>
                        <p className="font-semibold text-sm text-gray-700">Beispiel:</p>
                        <div className="bg-blue-50 rounded p-3 font-mono text-sm text-blue-900">
                          {formula.example}
                        </div>
                      </div>
                    )}
                    {formula.variables && (
                      <div>
                        <p className="font-semibold text-sm text-gray-700">Variablen:</p>
                        <div className="space-y-2 mt-2">
                          {Object.entries(formula.variables).map(([key, value]) => (
                            <div key={key} className="flex items-start">
                              <code className="bg-gray-200 px-2 py-1 rounded text-xs mr-2">{key}</code>
                              <span className="text-sm text-gray-600">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="font-bold text-lg mb-3 flex items-center">
              <Calculator className="w-5 h-5 mr-2 text-yellow-600" />
              Spezielle Berechnungen
            </h4>
            <div className="space-y-4">
              <div>
                <h5 className="font-semibold">Monte Carlo Simulation</h5>
                <p className="text-sm text-gray-600">
                  10.000+ Szenarien mit zufälligen Variablen für Mietsteigerung (1-3%), Leerstand (0-10%), 
                  Zinssätze (2-5%), Inflation (1-3%), Wertsteigerung (2-5%) und Instandhaltung (0.5-2%).
                </p>
              </div>
              <div>
                <h5 className="font-semibold">Spekulationsfrist (§23 EStG)</h5>
                <p className="text-sm text-gray-600">
                  10 Jahre ab Kaufdatum. Verkauf innerhalb dieser Frist führt zu Besteuerung des 
                  Veräußerungsgewinns mit dem persönlichen Steuersatz.
                </p>
              </div>
              <div>
                <h5 className="font-semibold">Drei-Objekt-Regel</h5>
                <p className="text-sm text-gray-600">
                  Verkauf von mehr als 3 Objekten innerhalb von 5 Jahren = gewerblicher Grundstückshandel 
                  mit Gewerbesteuerpflicht.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'workflow',
      title: 'Arbeitsablauf',
      icon: TrendingUp,
      content: (
        <div className="space-y-6">
          <div className="prose max-w-none">
            <h3 className="text-xl font-bold mb-4">Schritt-für-Schritt Anleitung</h3>
            <p className="text-gray-600 mb-6">
              So nutzen Sie Immobilien-Analyzer Pro optimal für Ihre Investitionsentscheidungen.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                step: 1,
                title: 'Immobilie eingeben',
                description: 'Geben Sie die Basisdaten der Immobilie ein: Kaufpreis, Wohnfläche, Mieteinnahmen.',
                tips: 'Nutzen Sie realistische Werte. Bei Unsicherheit lieber konservativ schätzen.'
              },
              {
                step: 2,
                title: 'Finanzierung konfigurieren',
                description: 'Legen Sie Eigenkapitalanteil, Zinssatz und Tilgung fest.',
                tips: 'Vergleichen Sie mehrere Bankangebote. Kalkulieren Sie mit Puffer für Zinsänderungen.'
              },
              {
                step: 3,
                title: 'Kosten erfassen',
                description: 'Tragen Sie Hausgeld, Instandhaltung und Verwaltungskosten ein.',
                tips: 'Planen Sie 10-15€/m² für Instandhaltung. Hausgeld aus Wirtschaftsplan übernehmen.'
              },
              {
                step: 4,
                title: 'Steueroptimierung prüfen',
                description: 'Nutzen Sie den Steueroptimizer für AfA und Strategien.',
                tips: 'Prüfen Sie Denkmalschutz-Status. Optimieren Sie Gebäude-/Grundstücksaufteilung.'
              },
              {
                step: 5,
                title: 'Compliance checken',
                description: 'Überprüfen Sie alle rechtlichen Anforderungen.',
                tips: 'Erstellen Sie Fristenliste. Dokumentieren Sie alle Nachweise.'
              },
              {
                step: 6,
                title: 'Risiken analysieren',
                description: 'Führen Sie Monte Carlo Simulation und Sensitivitätsanalyse durch.',
                tips: 'Fokus auf Worst-Case Szenarien. Prüfen Sie P10 und P90 Werte.'
              },
              {
                step: 7,
                title: 'Portfolio hinzufügen',
                description: 'Speichern Sie gute Objekte in Ihrem Portfolio.',
                tips: 'Nutzen Sie Favoriten für Top-Objekte. Vergleichen Sie bis zu 5 Immobilien.'
              },
              {
                step: 8,
                title: 'Reports erstellen',
                description: 'Exportieren Sie professionelle Analysen für Bank oder Investoren.',
                tips: 'PDF für Präsentationen, Excel für detaillierte Analyse.'
              }
            ].map((item) => (
              <div key={item.step} className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg mb-1">{item.title}</h4>
                  <p className="text-gray-600 mb-2">{item.description}</p>
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-sm text-green-800">
                      <strong>💡 Tipp:</strong> {item.tips}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="font-bold text-lg mb-3">Best Practices</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 mr-2" />
                <span className="text-sm">Immer mehrere Szenarien durchrechnen (optimistisch, realistisch, pessimistisch)</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 mr-2" />
                <span className="text-sm">Regelmäßig Portfolio-Performance überprüfen und anpassen</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 mr-2" />
                <span className="text-sm">Steuerliche Änderungen im Blick behalten</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 mr-2" />
                <span className="text-sm">Dokumentation für Finanzamt vollständig führen</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'technology',
      title: 'Technologie',
      icon: Code,
      content: (
        <div className="space-y-6">
          <div className="prose max-w-none">
            <h3 className="text-xl font-bold mb-4">Tech Stack & Architektur</h3>
            <p className="text-gray-600 mb-6">
              Modernste Technologien für maximale Performance und Benutzerfreundlichkeit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-bold text-lg mb-4 flex items-center">
                <Code className="w-5 h-5 mr-2 text-blue-600" />
                Frontend Stack
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">React</span>
                  <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">v19.1.1</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">TypeScript</span>
                  <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">v4.9.5</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">TailwindCSS</span>
                  <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">v3.4.17</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Chart.js</span>
                  <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">v4.5.0</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">React Window</span>
                  <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">v1.8.10</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-bold text-lg mb-4 flex items-center">
                <Database className="w-5 h-5 mr-2 text-green-600" />
                Data & Storage
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">LocalStorage</span>
                  <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">Portfolio</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">IndexedDB</span>
                  <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">Versions</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Service Worker</span>
                  <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">Cache</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Web Workers</span>
                  <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">Compute</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">CSV/XLSX</span>
                  <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">Export</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
            <h4 className="font-bold text-lg mb-4 flex items-center">
              <Zap className="w-5 h-5 mr-2 text-purple-600" />
              Performance Optimierungen
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="font-semibold mb-2">Code Splitting</h5>
                <p className="text-sm text-gray-600">
                  Lazy Loading für große Komponenten reduziert Initial Bundle Size auf unter 500KB.
                </p>
              </div>
              <div>
                <h5 className="font-semibold mb-2">Virtual DOM</h5>
                <p className="text-sm text-gray-600">
                  React.memo und useMemo für optimale Rendering-Performance.
                </p>
              </div>
              <div>
                <h5 className="font-semibold mb-2">Background Processing</h5>
                <p className="text-sm text-gray-600">
                  Web Workers für Berechnungen über 100ms ohne UI-Blockierung.
                </p>
              </div>
              <div>
                <h5 className="font-semibold mb-2">Caching Strategy</h5>
                <p className="text-sm text-gray-600">
                  Service Worker mit Cache-First Strategy für statische Assets.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="font-bold text-lg mb-3 flex items-center">
              <Globe className="w-5 h-5 mr-2 text-gray-600" />
              Browser Kompatibilität
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="text-center">
                <div className="text-2xl mb-1">🟢</div>
                <p className="text-sm font-semibold">Chrome 90+</p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-1">🟢</div>
                <p className="text-sm font-semibold">Firefox 88+</p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-1">🟢</div>
                <p className="text-sm font-semibold">Safari 14+</p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-1">🟢</div>
                <p className="text-sm font-semibold">Edge 90+</p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-1">🔴</div>
                <p className="text-sm font-semibold">IE (Nicht unterstützt)</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'roadmap',
      title: 'Roadmap',
      icon: Rocket,
      content: (
        <div className="space-y-6">
          <div className="prose max-w-none">
            <h3 className="text-xl font-bold mb-4">Entwicklungs-Roadmap</h3>
            <p className="text-gray-600 mb-6">
              Unsere Vision für die Zukunft von Immobilien-Analyzer Pro.
            </p>
          </div>

          <div className="space-y-8">
            {/* Current Version */}
            <div className="relative">
              <div className="absolute left-8 top-12 bottom-0 w-0.5 bg-green-300"></div>
              <div className="bg-green-50 border border-green-300 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    v4.0
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Aktuelle Version</h4>
                    <p className="text-sm text-gray-600">August 2025</p>
                  </div>
                </div>
                <ul className="space-y-1 text-sm">
                  <li>✅ Deutsche Steueroptimierung</li>
                  <li>✅ Legal Compliance Center</li>
                  <li>✅ Monte Carlo Simulation</li>
                  <li>✅ KI-Mietpreisvorhersage</li>
                </ul>
              </div>
            </div>

            {/* Q3 2025 */}
            <div className="relative">
              <div className="absolute left-8 top-12 bottom-0 w-0.5 bg-blue-300"></div>
              <div className="bg-blue-50 border border-blue-300 rounded-lg p-6 ml-12">
                <div className="flex items-center mb-3">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    Q3
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Version 4.1</h4>
                    <p className="text-sm text-gray-600">Q3 2025</p>
                  </div>
                </div>
                <ul className="space-y-1 text-sm">
                  <li>🔄 ImmoScout24 API Integration</li>
                  <li>🔄 GPT-4 Analyse-Assistent</li>
                  <li>🔄 Blockchain Smart Contracts</li>
                  <li>🔄 3D Visualisierungen</li>
                </ul>
              </div>
            </div>

            {/* Q4 2025 */}
            <div className="relative">
              <div className="absolute left-8 top-12 bottom-0 w-0.5 bg-gray-300"></div>
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 ml-12">
                <div className="flex items-center mb-3">
                  <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    Q4
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Version 5.0</h4>
                    <p className="text-sm text-gray-600">Q4 2025</p>
                  </div>
                </div>
                <ul className="space-y-1 text-sm">
                  <li>📱 Native Mobile Apps</li>
                  <li>☁️ Cloud Sync</li>
                  <li>🏦 Banking APIs</li>
                  <li>🌍 Multi-Language</li>
                </ul>
              </div>
            </div>

            {/* 2026 */}
            <div className="relative">
              <div className="bg-purple-50 border border-purple-300 rounded-lg p-6 ml-12">
                <div className="flex items-center mb-3">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    2026
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Vision 2026</h4>
                    <p className="text-sm text-gray-600">Die Zukunft</p>
                  </div>
                </div>
                <ul className="space-y-1 text-sm">
                  <li>🚀 AI-Vollautomatisierung</li>
                  <li>🌐 Europaweite Expansion</li>
                  <li>🤝 B2B Enterprise Solutions</li>
                  <li>📊 Predictive Analytics</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-8 text-white">
            <h4 className="font-bold text-xl mb-3">Werden Sie Teil der Zukunft!</h4>
            <p className="mb-4">
              Wir suchen ständig nach talentierten Entwicklern, Immobilienexperten und Visionären, 
              die mit uns die Zukunft der Immobilienanalyse gestalten wollen.
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="bg-white text-indigo-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100">
                Zum Team
              </button>
              <button className="border border-white px-4 py-2 rounded-lg font-semibold hover:bg-white/20">
                Feedback geben
              </button>
              <button className="border border-white px-4 py-2 rounded-lg font-semibold hover:bg-white/20">
                GitHub Star ⭐
              </button>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-lg p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <BookOpen className="w-10 h-10 mr-3" />
              Dokumentation & Handbuch
            </h1>
            <p className="text-indigo-100 mt-2">
              Alles was Sie über Immobilien-Analyzer Pro wissen müssen - von den Entwicklern für Sie.
            </p>
          </div>
          <div className="text-center">
            <Coffee className="w-16 h-16 mb-2 opacity-80" />
            <p className="text-sm opacity-90">Made with ❤️ and ☕</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-2">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => toggleSection(section.id)}
                className={`px-4 py-2 rounded-lg font-medium flex items-center transition-all ${
                  expandedSection === section.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {section.title}
                {expandedSection === section.id ? (
                  <ChevronDown className="w-4 h-4 ml-2" />
                ) : (
                  <ChevronRight className="w-4 h-4 ml-2" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {sections.map((section) => (
          <div
            key={section.id}
            className={`transition-all duration-500 ${
              expandedSection === section.id ? 'block' : 'hidden'
            }`}
          >
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              {section.content}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
        <p className="text-sm text-gray-600 mb-3">
          Immobilien-Analyzer Pro v4.0 © 2025 - Entwickelt von Gregorio Izquierdo & Fernando Izeta
        </p>
        <div className="flex justify-center space-x-4">
          <a href="https://github.com/ferone/immobilien4" className="text-gray-500 hover:text-gray-700">
            <Github className="w-5 h-5" />
          </a>
          <a href="#" className="text-gray-500 hover:text-gray-700">
            <Linkedin className="w-5 h-5" />
          </a>
          <a href="mailto:support@immobilien-analyzer.de" className="text-gray-500 hover:text-gray-700">
            <Mail className="w-5 h-5" />
          </a>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          "Building the future of real estate investment, one line of code at a time."
        </p>
      </div>
    </div>
  );
};

export default Readme;